/**
 * wd-product.js — Product page interactions (pricing tooltip, first-video autoplay, lightbox)
 */
(function () {
  let skipNextClickToggle = false;

  function getTooltipWrap(element) {
    return element?.closest?.('.wd-product-pricing__tooltip-wrap');
  }

  function isTooltipTrigger(element) {
    return Boolean(
      element?.closest?.('.wd-product-pricing__tooltip-trigger') ||
        element?.closest?.('.wd-product-pricing__tooltip-icon')
    );
  }

  function setTooltipOpen(wrap, open) {
    const trigger = wrap.querySelector('.wd-product-pricing__tooltip-trigger');
    const tooltip = wrap.querySelector('.wd-product-pricing__tooltip');

    if (!trigger || !tooltip) return;

    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    tooltip.setAttribute('aria-hidden', open ? 'false' : 'true');
    wrap.classList.toggle('is-tooltip-open', open);
  }

  function closeAllTooltips(exceptWrap) {
    document.querySelectorAll('.wd-product-pricing__tooltip-wrap.is-tooltip-open').forEach((wrap) => {
      if (wrap !== exceptWrap) {
        setTooltipOpen(wrap, false);
      }
    });
  }

  document.addEventListener('click', (event) => {
    const wrap = getTooltipWrap(event.target);

    if (wrap && isTooltipTrigger(event.target)) {
      event.preventDefault();
      event.stopPropagation();

      if (skipNextClickToggle) {
        skipNextClickToggle = false;
        return;
      }

      const isOpen = wrap.classList.contains('is-tooltip-open');
      closeAllTooltips(wrap);
      setTooltipOpen(wrap, !isOpen);
      return;
    }

    closeAllTooltips();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const wrap = getTooltipWrap(event.target);
    if (!wrap || !isTooltipTrigger(event.target)) return;

    event.preventDefault();

    const isOpen = wrap.classList.contains('is-tooltip-open');
    closeAllTooltips(wrap);
    setTooltipOpen(wrap, !isOpen);
    skipNextClickToggle = false;
  });

  document.addEventListener('focusin', (event) => {
    const wrap = getTooltipWrap(event.target);
    if (!wrap || !isTooltipTrigger(event.target)) return;

    closeAllTooltips(wrap);
    setTooltipOpen(wrap, true);
    skipNextClickToggle = true;
  });

  document.addEventListener('focusout', (event) => {
    const wrap = getTooltipWrap(event.target);
    if (!wrap || !isTooltipTrigger(event.target)) return;

    const next = event.relatedTarget;
    if (next && wrap.contains(next)) return;

    setTooltipOpen(wrap, false);
    skipNextClickToggle = false;
  });

  function includesLightboxVideo(gallery) {
    return gallery?.getAttribute('lightbox-content') === 'images_and_video';
  }

  function isZoomableMediaType(type, includeVideo) {
    if (type === 'image') return true;
    return includeVideo && (type === 'video' || type === 'external_video');
  }

  function getLightboxCells(gallery) {
    const includeVideo = includesLightboxVideo(gallery);
    return gallery.carousel.cells.filter((cell) =>
      isZoomableMediaType(cell.getAttribute('data-media-type'), includeVideo)
    );
  }

  function buildImageSlide(image) {
    return {
      thumbnailElement: image,
      src: image.currentSrc || image.src,
      srcset: image.srcset,
      msrc: image.currentSrc || image.src,
      width: parseInt(image.getAttribute('width'), 10) || image.naturalWidth || 1,
      height: parseInt(image.getAttribute('height'), 10) || image.naturalHeight || 1,
      alt: image.alt,
      thumbCropped: true,
    };
  }

  function withAutoplayParams(src) {
    if (!src) return src;

    try {
      const url = new URL(src, window.location.origin);
      const host = url.hostname;

      if (host.includes('youtube.com') || host.includes('youtu.be') || host.includes('youtube-nocookie.com')) {
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('mute', '1');
        url.searchParams.set('playsinline', '1');
        url.searchParams.set('loop', '1');
        url.searchParams.set('controls', '0');
      } else if (host.includes('vimeo.com')) {
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('muted', '1');
        url.searchParams.set('loop', '1');
        url.searchParams.set('background', '1');
      }

      return url.toString();
    } catch (_error) {
      return src;
    }
  }

  function buildVideoSlide(cell) {
    const type = cell.getAttribute('data-media-type');
    const preview = cell.querySelector('img');
    const videoMedia = cell.querySelector('video-media');
    const aspectRatio =
      parseFloat(videoMedia?.style?.getPropertyValue('--aspect-ratio')) ||
      (preview?.naturalWidth && preview?.naturalHeight
        ? preview.naturalWidth / preview.naturalHeight
        : 16 / 9);
    let width = preview?.naturalWidth || parseInt(preview?.getAttribute('width'), 10) || 1920;
    let height = Math.round(width / aspectRatio) || preview?.naturalHeight || 1080;
    let html = '';

    if (type === 'video') {
      const video = cell.querySelector('video');
      if (video) {
        width = video.videoWidth || parseInt(video.getAttribute('width'), 10) || width;
        height = video.videoHeight || parseInt(video.getAttribute('height'), 10) || height;

        const wrap = document.createElement('div');
        wrap.className = 'wd-pswp-video';

        const slideVideo = document.createElement('video');
        slideVideo.className = 'wd-pswp-video__el';
        slideVideo.setAttribute('muted', '');
        slideVideo.setAttribute('autoplay', '');
        slideVideo.setAttribute('loop', '');
        slideVideo.setAttribute('playsinline', '');
        slideVideo.setAttribute('preload', 'auto');

        const poster = video.getAttribute('poster') || preview?.currentSrc || preview?.src || '';
        if (poster) {
          slideVideo.setAttribute('poster', poster);
        }

        const directSrc = video.currentSrc || video.getAttribute('src') || video.src || '';
        if (directSrc) {
          slideVideo.setAttribute('src', directSrc);
        } else {
          video.querySelectorAll('source').forEach((source) => {
            const src = source.getAttribute('src') || source.src;
            if (!src) return;

            const slideSource = document.createElement('source');
            slideSource.setAttribute('src', src);
            const sourceType = source.getAttribute('type');
            if (sourceType) {
              slideSource.setAttribute('type', sourceType);
            }
            slideVideo.appendChild(slideSource);
          });
        }

        wrap.appendChild(slideVideo);
        html = wrap.outerHTML;
      }
    } else {
      const iframe = cell.querySelector('iframe');
      const template = cell.querySelector('template');
      let iframeNode = null;

      if (iframe) {
        iframeNode = iframe.cloneNode(true);
      } else if (template) {
        const templateIframe = template.content.querySelector('iframe');
        if (templateIframe) {
          iframeNode = templateIframe.cloneNode(true);
        }
      }

      if (iframeNode) {
        iframeNode.setAttribute('src', withAutoplayParams(iframeNode.getAttribute('src') || iframeNode.src));
        iframeNode.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');

        const wrap = document.createElement('div');
        wrap.className = 'wd-pswp-video wd-pswp-video--external';
        wrap.appendChild(iframeNode);
        html = wrap.outerHTML;
      }
    }

    if (!html && preview) {
      return buildImageSlide(preview);
    }

    return {
      html,
      type: 'html',
      width,
      height,
      thumbnailElement: preview || cell,
      thumbCropped: true,
    };
  }

  function pauseLightboxMedia(root) {
    if (!root) return;

    const video = root.querySelector('video');
    if (video && !video.paused) {
      video.pause();
    }

    const iframe = root.querySelector('iframe');
    if (iframe) {
      const currentSrc = iframe.getAttribute('src') || iframe.src || '';
      if (currentSrc) {
        iframe.dataset.wdLightboxSrc = currentSrc;
      }
      iframe.removeAttribute('src');
      iframe.src = '';
    }
  }

  function stopAllLightboxMedia(lightbox) {
    const root = lightbox.pswp?.element;
    if (!root) return;

    root.querySelectorAll('video').forEach((video) => {
      if (!video.paused) video.pause();
    });

    root.querySelectorAll('iframe').forEach((iframe) => {
      const currentSrc = iframe.getAttribute('src') || iframe.src || '';
      if (currentSrc) {
        iframe.dataset.wdLightboxSrc = currentSrc;
      }
      iframe.removeAttribute('src');
      iframe.src = '';
    });
  }

  function playLightboxMedia(root) {
    if (!root) return;

    const video = root.querySelector('video');
    if (video) {
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('loop', '');
      video.removeAttribute('controls');

      const tryPlay = () => {
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      };

      tryPlay();
      if (video.readyState < 2) {
        video.addEventListener('loadeddata', tryPlay, { once: true });
        video.addEventListener('canplay', tryPlay, { once: true });
      }
    }

    const iframe = root.querySelector('iframe');
    if (iframe) {
      const storedSrc = iframe.dataset.wdLightboxSrc || iframe.getAttribute('src') || iframe.src;
      const nextSrc = withAutoplayParams(storedSrc);
      if (nextSrc) {
        iframe.dataset.wdLightboxSrc = nextSrc;
        if (iframe.src !== nextSrc) {
          iframe.src = nextSrc;
        }
      }
    }
  }

  function bindLightboxVideoPlayback(lightbox) {
    if (!lightbox || lightbox.__wdVideoBound) return;
    lightbox.__wdVideoBound = true;

    let activeRoot = null;

    const activateRoot = (root) => {
      if (!root) return;

      if (activeRoot && activeRoot !== root) {
        pauseLightboxMedia(activeRoot);
      }

      activeRoot = root;
      playLightboxMedia(root);
    };

    const playCurrent = () => {
      const slide = lightbox.pswp?.currSlide;
      activateRoot(slide?.content?.element || slide?.container);
    };

    lightbox.on('change', playCurrent);
    lightbox.on('openingAnimationEnd', playCurrent);
    lightbox.on('contentActivate', ({ content }) => {
      activateRoot(content?.element);
    });
    lightbox.on('close', () => {
      if (activeRoot) {
        pauseLightboxMedia(activeRoot);
      }
      stopAllLightboxMedia(lightbox);
      activeRoot = null;
    });
  }

  // The arrows and close button live in PhotoSwipe's own bar, which is positioned against the
  // modal. Tagging them and publishing the displayed image rect lets wd-product.css anchor them
  // to the image instead.
  function bindLightboxControlPositions(lightbox) {
    if (!lightbox || lightbox.__wdControlsBound) return;
    lightbox.__wdControlsBound = true;

    // PhotoSwipe only falls back to its own pswp__button--{name} class when no custom className
    // is given, and theme.js gives one, so tag the controls by the name they were registered
    // under as PhotoSwipe builds them.
    lightbox.on('uiElementCreate', ({ data }) => {
      const role = { 'previous-button': 'prev', 'next-button': 'next', 'close-button': 'close' }[
        data.name
      ];

      if (role) {
        data.className += ` wd-pswp-control wd-pswp-control--${role}`;
      }
    });

    const update = () => {
      const pswp = lightbox.pswp;
      const root = pswp?.element;
      const slide = pswp?.currSlide;
      const zoom = slide?.zoomLevels?.initial;
      const panArea = slide?.panAreaSize;
      if (!root || !zoom || !panArea) return;

      // PhotoSwipe detaches and swaps the slide image around its animations, so the element
      // cannot be measured. Derive the fitted rect instead: the image is centred in the pan area
      // at the initial zoom level. slide.bounds.center is deliberately not used, because
      // PhotoSwipe recalculates it against the live zoom level, which would drag the controls
      // along as the shopper zooms in.
      const width = slide.width * zoom;
      const height = slide.height * zoom;
      if (!width || !height) return;

      root.style.setProperty('--wd-pswp-image-left', `${(panArea.x - width) / 2}px`);
      root.style.setProperty('--wd-pswp-image-top', `${(panArea.y - height) / 2}px`);
      root.style.setProperty('--wd-pswp-image-width', `${width}px`);
      root.style.setProperty('--wd-pswp-image-height', `${height}px`);

      // Switch to the custom placement only once the rect is known, so failing to read the slide
      // geometry leaves PhotoSwipe's own control bar in place instead of stacking the controls.
      root.classList.add('wd-pswp--anchored');
    };

    lightbox.on('openingAnimationEnd', update);
    lightbox.on('resize', update);
    lightbox.on('change', update);
  }

  function buildLightboxDataSource(gallery) {
    return getLightboxCells(gallery).map((cell) => {
      const type = cell.getAttribute('data-media-type');
      if (type === 'image') {
        const image = cell.querySelector(':scope > img') || cell.querySelector('img');
        return image ? buildImageSlide(image) : null;
      }
      return buildVideoSlide(cell);
    }).filter(Boolean);
  }

  function syncZoomButtonVisibility(gallery) {
    const zoomButton = gallery.querySelector('.product-gallery__zoom-button');
    if (!zoomButton || !includesLightboxVideo(gallery)) return;

    const type = gallery.carousel?.selectedCell?.getAttribute('data-media-type');
    zoomButton.classList.toggle(
      'product-gallery__zoom-button--hidden',
      !isZoomableMediaType(type, true)
    );
  }

  function playInitialGalleryVideo() {
    const wrap = document.querySelector('.wd-product-gallery--first-video') || document.querySelector('.wd-product-gallery');
    if (!wrap) return;

    const productGallery = wrap.querySelector('product-gallery');
    const videoHosts = wrap.querySelectorAll(
      '.product-gallery__media.is-initial[data-media-type="video"] video-media, .product-gallery__media.is-initial[data-media-type="external_video"] video-media'
    );

    videoHosts.forEach((videoMedia) => {
      const video = videoMedia.querySelector('video');
      if (video) {
        video.controls = false;
        video.loop = true;
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.setAttribute('loop', '');
        video.removeAttribute('controls');
      }

      if (typeof videoMedia.play === 'function') {
        videoMedia.play();
      } else {
        video?.play().catch(() => {});
      }
    });

    const initialMedia = wrap.querySelector('.product-gallery__media.is-initial');
    const initialType = initialMedia?.getAttribute('data-media-type');
    if (
      (initialType === 'video' || initialType === 'external_video') &&
      !includesLightboxVideo(productGallery)
    ) {
      productGallery?.querySelector('.product-gallery__zoom-button')?.classList.add('product-gallery__zoom-button--hidden');
    } else {
      syncZoomButtonVisibility(productGallery);
    }
  }

  function bindGalleryImageZoom() {
    document.querySelectorAll('product-gallery[allow-zoom]').forEach((gallery) => {
      const carousel = gallery.querySelector('.product-gallery__carousel');
      if (!carousel || carousel.dataset.wdZoomBound === 'true') return;

      carousel.dataset.wdZoomBound = 'true';
      // The theme cancels gesturestart on the gallery to open the lightbox. Stop that so Safari can zoom the page.
      const allowPagePinch = (event) => {
        event.stopPropagation();
      };
      carousel.addEventListener('gesturestart', allowPagePinch, true);
      carousel.addEventListener('gesturechange', allowPagePinch, true);
      carousel.addEventListener('gestureend', allowPagePinch, true);
      carousel.addEventListener(
        'click',
        (event) => {
          const mdQuery = window.themeVariables?.mediaQueries?.md || 'screen and (min-width: 1000px)';
          if (!window.matchMedia(mdQuery).matches) return;

          const media = event.target.closest('.product-gallery__media');
          if (!media) return;

          const includeVideo = includesLightboxVideo(gallery);
          const type = media.getAttribute('data-media-type');
          if (!isZoomableMediaType(type, includeVideo)) return;
          if (type === 'image' && event.target.tagName !== 'IMG') return;

          event.preventDefault();
          event.stopImmediatePropagation();

          const cells = getLightboxCells(gallery);
          const index = cells.indexOf(media);
          if (index < 0) return;

          if (typeof gallery.openLightBox === 'function') {
            gallery.openLightBox(index);
          } else {
            gallery.dispatchEvent(
              new CustomEvent('lightbox:open', {
                bubbles: true,
                detail: { index },
              })
            );
          }
        },
        true
      );

      if (includesLightboxVideo(gallery)) {
        carousel.addEventListener('carousel:settle', () => {
          requestAnimationFrame(() => syncZoomButtonVisibility(gallery));
        });
        syncZoomButtonVisibility(gallery);
      }
    });
  }

  function patchProductGalleryLightBox() {
    const apply = () => {
      const Gallery = customElements.get('product-gallery');
      if (!Gallery || Gallery.prototype.__wdZoomPatched) return;

      Gallery.prototype.__wdZoomPatched = true;
      Gallery.prototype.openLightBox = function openLightBox(index) {
        const cells = getLightboxCells(this);
        const dataSource = buildLightboxDataSource(this);

        let lightbox = null;
        try {
          lightbox = this.lightBox || this.lightbox || null;
        } catch (_error) {
          return;
        }
        if (!lightbox || typeof lightbox.loadAndOpen !== 'function') return;

        bindLightboxVideoPlayback(lightbox);
        bindLightboxControlPositions(lightbox);

        let resolvedIndex = index;
        if (resolvedIndex == null) {
          resolvedIndex = cells.indexOf(this.carousel.selectedCell);
        }
        if (resolvedIndex < 0) resolvedIndex = 0;

        lightbox.loadAndOpen(resolvedIndex, dataSource);
      };
    };

    if (customElements.get('product-gallery')) {
      apply();
    } else {
      customElements.whenDefined('product-gallery').then(apply);
    }
  }

  function bindProductChangeAutoplay() {
    document.querySelectorAll('product-rerender[observe-form]').forEach((el) => {
      const form = document.forms[el.getAttribute('observe-form')];
      if (!form || form.dataset.wdGalleryAutoplayBound === 'true') return;

      form.dataset.wdGalleryAutoplayBound = 'true';
      form.addEventListener('product:rerender', (event) => {
        if (!event.detail?.productChange) return;

        requestAnimationFrame(() => {
          playInitialGalleryVideo();
          bindGalleryImageZoom();
          bindProductChangeAutoplay();
        });
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    patchProductGalleryLightBox();
    playInitialGalleryVideo();
    bindGalleryImageZoom();
    bindProductChangeAutoplay();
  });
})();
